/**
 * BitMiner24 — Motion FX
 * Animation layer on top of Motion (window.Motion, UMD build in assets/motion.min.js).
 *
 * Every helper degrades gracefully: if Motion is missing, fails, or the user
 * prefers reduced motion, the result is an instant non-animated update — never
 * hidden or broken content.
 *
 * Exposed as window.BMFx. Pure helpers (parseNumeric/formatNumeric) are also
 * exported for Node unit tests (BitMiner-Theme/tests/bm-motion-fx.test.cjs).
 */
(function (window, document) {
  'use strict';

  // --- pure helpers ---------------------------------------------------------

  /**
   * Group and decimal separator of the current document language. A count-up on
   * the English storefront reads "178,761" and has to write it back the same way,
   * not as the German "178.761".
   */
  function numberSymbols() {
    var lang = 'de';
    if (document && document.documentElement) {
      lang = (document.documentElement.getAttribute('lang') || 'de').slice(0, 2).toLowerCase();
    }
    return lang === 'de' ? { group: '.', decimal: ',' } : { group: ',', decimal: '.' };
  }

  function escapeForClass(ch) {
    return ch === '.' ? '\\.' : ch;
  }

  /**
   * Parses a formatted number out of a display string, in the notation of the
   * given symbols (document language by default).
   *   "1.500+"      -> { prefix:'',  num:1500,  decimals:0, grouped:true,  suffix:'+' }
   *   "~14"         -> { prefix:'~', num:14,    decimals:0, grouped:false, suffix:'' }
   *   "4,95"        -> { prefix:'',  num:4.95,  decimals:2, grouped:false, suffix:'' }
   *   "195.000 €"   -> { prefix:'',  num:195000, decimals:0, grouped:true, suffix:' €' }
   * Returns null when there is no number or more than one (e.g. "2-3", "--").
   */
  function parseNumeric(text, symbols) {
    if (typeof text !== 'string') return null;
    var sym = symbols || numberSymbols();
    var g = escapeForClass(sym.group);
    var d = escapeForClass(sym.decimal);
    var matches = text.match(new RegExp('\\d[\\d' + g + ']*(?:' + d + '\\d+)?', 'g'));
    if (!matches || matches.length !== 1) return null;
    // a trailing group separator belongs to the sentence, not to the number
    var raw = matches[0].replace(new RegExp(g + '$'), '');
    var idx = text.indexOf(raw);
    var intPart = raw, decPart = '';
    var decIdx = raw.indexOf(sym.decimal);
    if (decIdx !== -1) { intPart = raw.slice(0, decIdx); decPart = raw.slice(decIdx + 1); }
    var num = parseFloat(intPart.split(sym.group).join('') + (decPart ? '.' + decPart : ''));
    if (!isFinite(num)) return null;
    return {
      prefix: text.slice(0, idx),
      num: num,
      decimals: decPart.length,
      grouped: intPart.indexOf(sym.group) !== -1,
      suffix: text.slice(idx + raw.length)
    };
  }

  /** Formats a number back into the shape described by a parseNumeric() spec. */
  function formatNumeric(value, spec, symbols) {
    var sym = symbols || numberSymbols();
    var neg = value < 0 ? '-' : '';
    var fixed = Math.abs(value).toFixed(spec.decimals);
    var parts = fixed.split('.');
    var intStr = parts[0];
    if (spec.grouped) intStr = intStr.replace(/\B(?=(\d{3})+(?!\d))/g, sym.group);
    return spec.prefix + neg + intStr + (parts[1] ? sym.decimal + parts[1] : '') + spec.suffix;
  }

  // --- environment ----------------------------------------------------------

  function prefersReduced() {
    try { return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); }
    catch (e) { return false; }
  }

  function lib() {
    var M = window.Motion;
    return M && typeof M.animate === 'function' ? M : null;
  }

  function onFinished(controls, fn) {
    if (!controls) return;
    if (typeof controls.then === 'function') controls.then(fn, function () {});
    else if (controls.finished && typeof controls.finished.then === 'function') controls.finished.then(fn, function () {});
  }

  // --- FX -------------------------------------------------------------------

  /**
   * Tweens an element's text content from its current numeric value to the
   * numeric value contained in newText (count-up/count-down). Falls back to an
   * instant swap when animation is not possible.
   * opts: { duration?: seconds, fromZero?: start at 0 when current text has no number }
   * Returns true when the displayed text changed, false when it was already up to date.
   */
  function tweenText(el, newText, opts) {
    opts = opts || {};
    if (!el || typeof newText !== 'string') return false;
    var current = el.textContent;
    if (current === newText) return false;
    var M = lib();
    var to = parseNumeric(newText);
    var from = parseNumeric(current);
    if (!from && opts.fromZero && to) from = { num: 0 };
    if (!M || prefersReduced() || !to || !from || from.num === to.num) {
      el.textContent = newText;
      return true;
    }
    if (el.__bmTween) { try { el.__bmTween.stop(); } catch (e) {} }
    try {
      var controls = M.animate(from.num, to.num, {
        duration: opts.duration != null ? opts.duration : 0.9,
        ease: [0.22, 1, 0.36, 1],
        onUpdate: function (v) { el.textContent = formatNumeric(v, to); }
      });
      el.__bmTween = controls;
      onFinished(controls, function () { el.textContent = newText; el.__bmTween = null; });
    } catch (e) {
      el.textContent = newText;
    }
    return true;
  }

  /** Short attention tick (scale pulse), e.g. after a live value update. */
  function flash(el) {
    var M = lib();
    if (!M || !el || prefersReduced()) return;
    try {
      // transform needs a box — only promote true inline elements (<em>/<span>),
      // never touch flex/grid/block elements (would break button layouts)
      if (window.getComputedStyle && getComputedStyle(el).display === 'inline') {
        el.style.display = 'inline-block';
      }
      M.animate(el, { scale: [1, 1.12, 1] }, { duration: 0.5, ease: 'easeOut' });
    } catch (e) {}
  }

  /**
   * Relative time for whole days, in the language of the document.
   * Kept as a BMFx member for the existing call sites; the implementation lives
   * in bm-format.js so there is exactly one of it.
   */
  function daysAgoText(days) {
    return window.BMFormat ? window.BMFormat.daysAgo(days) : String(days);
  }

  // --- hash-scramble text (React-Bits "Decrypted Text", ported) ---------------

  var SCRAMBLE_CHARSET = 'ABCDEF0123456789';

  /**
   * Pure scramble frame: first floor(progress * length) characters are settled,
   * remaining alphanumerics are replaced via rand() from a hex charset (hash
   * look). Non-alphanumerics (spaces, ·, €) keep their shape.
   */
  function scrambleStep(target, progress, rand) {
    if (typeof target !== 'string') return '';
    var p = Math.max(0, Math.min(1, progress));
    var settled = Math.floor(p * target.length);
    var out = '';
    for (var i = 0; i < target.length; i++) {
      var ch = target.charAt(i);
      if (i < settled || !/[0-9A-Za-z]/.test(ch)) out += ch;
      else out += SCRAMBLE_CHARSET.charAt(Math.floor(rand() * SCRAMBLE_CHARSET.length));
    }
    return out;
  }

  /** One-shot hash-scramble that settles left-to-right onto the existing text. */
  function scrambleText(el, opts) {
    opts = opts || {};
    if (!el || prefersReduced() || !window.requestAnimationFrame) return;
    var target = el.textContent;
    if (!target || !target.trim()) return;
    var duration = (opts.duration != null ? opts.duration : 0.9) * 1000;
    if (el.offsetWidth) el.style.minWidth = el.offsetWidth + 'px'; // keep pill width stable
    var start = null;
    function frame(ts) {
      if (start === null) start = ts;
      var p = (ts - start) / duration;
      if (p >= 1) { el.textContent = target; el.style.minWidth = ''; return; }
      el.textContent = scrambleStep(target, p, Math.random);
      window.requestAnimationFrame(frame);
    }
    window.requestAnimationFrame(frame);
  }

  function initScrambles() {
    if (!document) return;
    var nodes = document.querySelectorAll('[data-bm-scramble]');
    Array.prototype.forEach.call(nodes, function (el) {
      if (el.__bmScrambled) return; // init() runs twice (DOM-ready + Motion-ready)
      el.__bmScrambled = true;
      scrambleText(el);
    });
  }

  // --- spotlight cards + CTA shimmer (React-Bits ports, injected children) ----

  // Styles live here (not in theme CSS) so removing this file removes the FX.
  // Child <span>s instead of ::after — theme buttons/cards already use pseudos.
  function injectStyles() {
    if (!document || document.getElementById('bm-fx-style')) return;
    var css = '' +
      '[data-bm-spotlight]{position:relative}' +
      '.bm-fx-spot{position:absolute;inset:0;border-radius:inherit;pointer-events:none;z-index:3;opacity:0;transition:opacity .25s ease;background:radial-gradient(320px circle at var(--bm-spot-x,50%) var(--bm-spot-y,50%),rgba(247,147,26,.12),transparent 65%)}' +
      '[data-bm-spotlight]:hover .bm-fx-spot{opacity:1}' +
      '[data-bm-shimmer]{position:relative;overflow:hidden}' +
      // transform-only keyframes — animating `left` would force layout every frame
      '.bm-fx-shimmer{position:absolute;top:-20%;bottom:-20%;left:0;width:50%;pointer-events:none;background:linear-gradient(105deg,transparent 20%,rgba(255,255,255,.28) 50%,transparent 80%);transform:translateX(-170%) skewX(-18deg);animation:bm-fx-shimmer 6s ease-in-out infinite}' +
      '@keyframes bm-fx-shimmer{0%,72%{transform:translateX(-170%) skewX(-18deg)}88%,100%{transform:translateX(270%) skewX(-18deg)}}' +
      '@media (prefers-reduced-motion: reduce){.bm-fx-shimmer,.bm-fx-spot{display:none}}';
    var s = document.createElement('style');
    s.id = 'bm-fx-style';
    s.textContent = css;
    document.head.appendChild(s);
  }

  function initSpotlights() {
    if (!document || prefersReduced()) return;
    var mq;
    try { mq = window.matchMedia('(hover: hover) and (pointer: fine)'); } catch (e) { return; }
    if (!mq || !mq.matches) return;
    var cards = document.querySelectorAll('[data-bm-spotlight]');
    Array.prototype.forEach.call(cards, function (card) {
      if (card.querySelector('.bm-fx-spot')) return;
      var spot = document.createElement('span');
      spot.className = 'bm-fx-spot';
      spot.setAttribute('aria-hidden', 'true');
      card.appendChild(spot);
      var pending = false, lastX = 0, lastY = 0;
      card.addEventListener('mousemove', function (e) {
        lastX = e.clientX; lastY = e.clientY;
        if (pending) return;
        pending = true;
        window.requestAnimationFrame(function () {
          pending = false;
          var r = card.getBoundingClientRect();
          card.style.setProperty('--bm-spot-x', (lastX - r.left) + 'px');
          card.style.setProperty('--bm-spot-y', (lastY - r.top) + 'px');
        });
      }, { passive: true });
    });
  }

  function initShimmers() {
    if (!document || prefersReduced()) return;
    var els = document.querySelectorAll('[data-bm-shimmer]');
    Array.prototype.forEach.call(els, function (el) {
      if (el.querySelector('.bm-fx-shimmer')) return;
      var bar = document.createElement('span');
      bar.className = 'bm-fx-shimmer';
      bar.setAttribute('aria-hidden', 'true');
      el.appendChild(bar);
    });
  }

  /**
   * Stagger-reveals a container's children the first time the container
   * scrolls into view. Children are never pre-hidden — if Motion is missing
   * the content simply stays visible.
   * mode: 'children' (default, fade + translateY) | 'fade' (opacity only,
   * safe for <tbody>/table rows) | 'self' (animate the element itself)
   */
  function revealInView(container, mode) {
    var M = lib();
    if (!M || !container || prefersReduced() || typeof M.inView !== 'function') return;
    var keyframes = mode === 'fade'
      ? { opacity: [0, 1] }
      : { opacity: [0, 1], transform: ['translateY(10px)', 'translateY(0px)'] };
    try {
      M.inView(container, function () {
        var list = mode === 'self'
          ? [container]
          : Array.prototype.slice.call(container.children);
        if (!list.length) return;
        try {
          M.animate(list, keyframes,
            { duration: 0.45, ease: 'easeOut', delay: M.stagger(0.07) }
          );
        } catch (e) {}
      }, { amount: 0.15 });
    } catch (e) {}
  }

  /**
   * Declarative scroll-reveal: every [data-bm-reveal] container stagger-reveals
   * its children when it first scrolls into view. Attribute value selects the
   * mode ('', 'fade', 'self' — see revealInView).
   */
  function initReveals() {
    var M = lib();
    if (!document || !M || prefersReduced() || typeof M.inView !== 'function') return;
    var nodes = document.querySelectorAll('[data-bm-reveal]');
    Array.prototype.forEach.call(nodes, function (el) {
      revealInView(el, el.getAttribute('data-bm-reveal') || 'children');
    });
  }

  /**
   * Count-up for static stats: every [data-bm-countup] element starts at 0 and
   * counts to its server-rendered value when it scrolls into view.
   * Without Motion / with reduced motion the server-rendered value stays as is.
   */
  function initCountUps() {
    var M = lib();
    if (!document || !M || prefersReduced() || typeof M.inView !== 'function') return;
    var nodes = document.querySelectorAll('[data-bm-countup]');
    Array.prototype.forEach.call(nodes, function (el) {
      var target = el.textContent.trim();
      var spec = parseNumeric(target);
      if (!spec || spec.num === 0) return;
      el.textContent = formatNumeric(0, spec);
      M.inView(el, function () {
        tweenText(el, target, { duration: 1.1 });
      }, { amount: 0.6 });
    });
  }

  var BMFx = {
    parseNumeric: parseNumeric,
    formatNumeric: formatNumeric,
    tweenText: tweenText,
    flash: flash,
    revealInView: revealInView,
    daysAgoText: daysAgoText,
    scrambleStep: scrambleStep,
    scrambleText: scrambleText
  };

  window.BMFx = BMFx;

  function init() {
    injectStyles();
    initCountUps();
    initReveals();
    initScrambles();
    initSpotlights();
    initShimmers();
  }

  // Motion (135 KB) is lazy-loaded after window load to keep its parse cost out
  // of the critical path. Until it arrives every helper falls back to instant
  // swaps; init() re-runs once the lib is ready (all inits are idempotent).
  function loadMotionLib() {
    if (window.Motion || !document || document.getElementById('bm-motion-lib') || !window.__bmMotionSrc) return;
    var s = document.createElement('script');
    s.id = 'bm-motion-lib';
    s.src = window.__bmMotionSrc;
    s.onload = function () { init(); };
    document.head.appendChild(s);
  }

  if (document) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
    else init();

    if (document.readyState === 'complete') loadMotionLib();
    else window.addEventListener('load', loadMotionLib);
  }

  // Node unit tests
  if (typeof module !== 'undefined' && module.exports) module.exports = BMFx;
})(
  typeof window !== 'undefined' ? window : globalThis,
  typeof document !== 'undefined' ? document : null
);
